// Shared Services Allocation page
import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, Stack, Paper, Grid, Divider, TextField, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
    SummaryGrid, SectionHeader, DrillTable, EmptyState, LoadingBlock,
    DonutChart, StatusPill, fmtMoney, fmtDate, CHART_COLORS,
} from '../agents/AgentUIKit';
import { listAllocations, createAllocation, postAllocation, reverseAllocation, listEntities } from './api';

const BASES = ['REVENUE_PCT', 'HEADCOUNT', 'SQUARE_FEET', 'DIRECT_COST', 'FIXED_AMOUNT', 'CUSTOM'];

export default function SharedServices() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [allocs, setAllocs] = useState([]);
    const [entities, setEntities] = useState([]);
    const [selected, setSelected] = useState(null);

    // Dialog
    const [dialog, setDialog] = useState(false);
    const [form, setForm] = useState({
        name: '', description: '', source_entity_id: '',
        total_amount: '', currency: 'USD',
        allocation_basis: 'REVENUE_PCT',
        period_start: '', period_end: '',
        targets: [],
    });
    const [targetInput, setTargetInput] = useState({ entity_id: '', basis_value: '' });

    const run = async (fn) => {
        setLoading(true); setError(null);
        try { return await fn(); }
        catch (e) { setError(e?.response?.data?.message || e.message); }
        finally { setLoading(false); }
    };

    const loadAll = async () => {
        const [r1, r2] = await Promise.allSettled([listAllocations(), listEntities()]);
        if (r1.status === 'fulfilled' && r1.value?.data?.success) setAllocs(r1.value.data.data);
        if (r2.status === 'fulfilled' && r2.value?.data?.success) setEntities(r2.value.data.data);
    };

    useEffect(() => { run(loadAll); }, []);  // eslint-disable-line

    const addTarget = () => {
        if (!targetInput.entity_id || !targetInput.basis_value) return;
        setForm({
            ...form,
            targets: [...form.targets, {
                entity_id: parseInt(targetInput.entity_id),
                basis_value: parseFloat(targetInput.basis_value),
            }],
        });
        setTargetInput({ entity_id: '', basis_value: '' });
    };

    const removeTarget = (idx) => {
        setForm({ ...form, targets: form.targets.filter((_, i) => i !== idx) });
    };

    const submit = async () => {
        if (!form.name || !form.source_entity_id || !form.total_amount || form.targets.length === 0) return;
        const body = { ...form, total_amount: parseFloat(form.total_amount) };
        const r = await run(() => createAllocation(body));
        if (r?.data?.success) {
            setDialog(false);
            setForm({
                name: '', description: '', source_entity_id: '',
                total_amount: '', currency: 'USD', allocation_basis: 'REVENUE_PCT',
                period_start: '', period_end: '', targets: [],
            });
            await loadAll();
        }
    };

    const handlePost = async (id) => {
        const r = await run(() => postAllocation(id));
        if (r?.data?.success) await loadAll();
    };

    const handleReverse = async (id) => {
        if (!window.confirm('Reverse this allocation?')) return;
        const r = await run(() => reverseAllocation(id));
        if (r?.data?.success) await loadAll();
    };

    const entityLabel = (id) => {
        const e = entities.find(x => x.id === id);
        return e ? e.entity_code : `#${id}`;
    };

    const summaryCards = [
        { label: 'Total Allocations', value: allocs.length, accent: 'primary' },
        { label: 'Draft', value: allocs.filter(a => a.status === 'DRAFT').length, accent: 'warning' },
        { label: 'Posted', value: allocs.filter(a => a.status === 'POSTED').length, accent: 'success' },
        { label: 'YTD Allocated', value: fmtMoney(allocs.filter(a => a.status === 'POSTED').reduce((s, a) => s + (a.total_amount || 0), 0)), accent: 'info' },
    ];

    const selectedDonut = selected?.targets_json
        ? selected.targets_json.map(t => ({ name: entityLabel(t.entity_id), value: t.amount }))
        : [];

    return (
        <Box sx={{ p: 2 }}>
            <Stack spacing={1.5}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h4" fontWeight={700}>Shared Services Allocation</Typography>
                        <Typography variant="caption" color="text.secondary">
                            Distribute corporate/shared costs across entities by revenue, headcount, or custom basis
                        </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                        <IconButton size="small" onClick={() => run(loadAll)}><RefreshIcon /></IconButton>
                        <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setDialog(true)}>
                            New Allocation
                        </Button>
                    </Stack>
                </Stack>
                {error && <Typography variant="caption" color="error">{error}</Typography>}

                <SummaryGrid cards={summaryCards} />
                <Divider />

                <Paper variant="outlined" sx={{ p: 1.5 }}>
                    <SectionHeader title="Allocations" />
                    {loading && !allocs.length ? <LoadingBlock rows={4} /> : (
                        <DrillTable
                            columns={[
                                { key: 'name', label: 'Name' },
                                { key: 'source_entity_id', label: 'Source', render: r => entityLabel(r.source_entity_id) },
                                { key: 'total_amount', label: 'Total', align: 'right', render: r => `${fmtMoney(r.total_amount)} ${r.currency}` },
                                { key: 'allocation_basis', label: 'Basis', render: r => <Chip size="small" variant="outlined" label={r.allocation_basis} /> },
                                { key: 'period_end', label: 'Period', render: r => `${fmtDate(r.period_start)} → ${fmtDate(r.period_end)}` },
                                { key: 'targets_count', label: 'Targets', align: 'right', render: r => (r.targets_json || []).length },
                                { key: 'status', label: 'Status', render: r => <StatusPill status={r.status} /> },
                                { key: 'actions', label: '', render: r => (
                                    <Stack direction="row" spacing={0.5}>
                                        <Button size="small" onClick={() => setSelected(r)}>View</Button>
                                        {r.status === 'DRAFT' && <Button size="small" variant="outlined" startIcon={<PlayArrowIcon fontSize="small" />} onClick={() => handlePost(r.id)}>Post</Button>}
                                        {r.status === 'POSTED' && <Button size="small" variant="outlined" color="warning" startIcon={<UndoIcon fontSize="small" />} onClick={() => handleReverse(r.id)}>Reverse</Button>}
                                    </Stack>
                                )},
                            ]}
                            rows={allocs}
                            maxHeight={420}
                            rowKeyField="id"
                            onRowClick={setSelected}
                        />
                    )}
                </Paper>

                {selected && (
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <SectionHeader
                            title={selected.name}
                            subtitle={`${entityLabel(selected.source_entity_id)} | ${fmtMoney(selected.total_amount)} ${selected.currency} | ${selected.allocation_basis}`}
                        />
                        <Grid container spacing={1}>
                            <Grid item xs={12} md={6}>
                                <DonutChart data={selectedDonut} height={240} formatter={v => fmtMoney(v)} />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <DrillTable
                                    columns={[
                                        { key: 'entity_id', label: 'Entity', render: r => entityLabel(r.entity_id) },
                                        { key: 'basis_value', label: 'Basis', align: 'right', render: r => r.basis_value.toFixed(2) },
                                        { key: 'pct', label: '%', align: 'right', render: r => `${(r.pct * 100).toFixed(1)}%` },
                                        { key: 'amount', label: 'Amount', align: 'right', render: r => <b>{fmtMoney(r.amount)}</b> },
                                    ]}
                                    rows={selected.targets_json || []}
                                />
                            </Grid>
                        </Grid>
                    </Paper>
                )}
            </Stack>

            {/* New Allocation Dialog */}
            <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="lg" fullWidth>
                <DialogTitle>New Shared Services Allocation</DialogTitle>
                <DialogContent dividers>
                    <Grid container spacing={1} sx={{ pt: 1 }}>
                        <Grid item xs={12} md={8}>
                            <TextField size="small" fullWidth label="Name" value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField size="small" fullWidth select label="Basis" value={form.allocation_basis}
                                onChange={e => setForm({ ...form, allocation_basis: e.target.value })}>
                                {BASES.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField size="small" fullWidth multiline rows={2} label="Description" value={form.description}
                                onChange={e => setForm({ ...form, description: e.target.value })} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <TextField size="small" fullWidth select label="Source Entity" value={form.source_entity_id}
                                onChange={e => setForm({ ...form, source_entity_id: e.target.value })}>
                                {entities.map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth type="number" label="Total Amount" value={form.total_amount}
                                onChange={e => setForm({ ...form, total_amount: e.target.value })} />
                        </Grid>
                        <Grid item xs={6} md={2}>
                            <TextField size="small" fullWidth label="Currency" value={form.currency}
                                onChange={e => setForm({ ...form, currency: e.target.value.toUpperCase().slice(0, 3) })} />
                        </Grid>
                        <Grid item xs={6} md={3}>
                            <TextField size="small" fullWidth type="date" label="Period Start" InputLabelProps={{ shrink: true }}
                                value={form.period_start} onChange={e => setForm({ ...form, period_start: e.target.value })} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField size="small" fullWidth type="date" label="Period End" InputLabelProps={{ shrink: true }}
                                value={form.period_end} onChange={e => setForm({ ...form, period_end: e.target.value })} />
                        </Grid>
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="caption" fontWeight={600}>Target Entities</Typography>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth select label="Target Entity" value={targetInput.entity_id}
                                onChange={e => setTargetInput({ ...targetInput, entity_id: e.target.value })}>
                                {entities.filter(e => e.id != form.source_entity_id).map(ent => <MenuItem key={ent.id} value={ent.id}>{ent.entity_code}</MenuItem>)}
                            </TextField>
                        </Grid>
                        <Grid item xs={6} md={4}>
                            <TextField size="small" fullWidth type="number" label="Basis Value" value={targetInput.basis_value}
                                onChange={e => setTargetInput({ ...targetInput, basis_value: e.target.value })}
                                helperText={`e.g. ${form.allocation_basis === 'HEADCOUNT' ? 'employees' : form.allocation_basis === 'REVENUE_PCT' ? '$ revenue' : 'value'}`} />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Button variant="outlined" size="small" fullWidth startIcon={<AddIcon />} onClick={addTarget}>Add Target</Button>
                        </Grid>
                        {form.targets.length > 0 && (
                            <Grid item xs={12}>
                                <DrillTable
                                    columns={[
                                        { key: 'entity_id', label: 'Entity', render: r => entityLabel(r.entity_id) },
                                        { key: 'basis_value', label: 'Basis Value', align: 'right' },
                                        { key: 'action', label: '', render: (_, i) => (
                                            <IconButton size="small" color="error" onClick={() => removeTarget(i)}><DeleteIcon fontSize="small" /></IconButton>
                                        )},
                                    ]}
                                    rows={form.targets}
                                />
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={submit}
                        disabled={!form.name || !form.source_entity_id || !form.total_amount || form.targets.length === 0 || loading}>
                        Create Allocation
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
